/**
 * The desk's typed client for the API's oRPC router. Types come from `@lsc/contract`; the desk
 * never imports the API package. Calls go to `/api/rpc` on the same origin (Caddy in production,
 * the Vite proxy in dev), so there is no CORS and no second hostname.
 *
 * Every call carries the signed-in staff member's portal access token. A 401 renews the token
 * once and retries; only a refused renewal or a second 401 signs the desk out (2026-10-23: a
 * request on the hour's edge used to sign reps out). A gate refusal (403 "This desk is for
 * Lifelong staff") signs out with the reason, and the login screen shows it.
 */
import { createORPCClient, ORPCError } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import { type ContractClient } from '@lsc/contract';
import { accessToken, refreshAccessToken, signOut } from './auth';

const link = new RPCLink({
  url: `${window.location.origin}/api/rpc`,
  headers: async () => {
    const token = await accessToken();
    return token ? { authorization: `Bearer ${token}` } : {};
  },
  fetch: (request, init) => authFetch(request, init),
});

export const rpc: ContractClient = createORPCClient(link);

type RefreshResult = Awaited<ReturnType<typeof refreshAccessToken>>;

/**
 * One API request with the sign-in rules: a 401 renews the token once and retries; the desk signs
 * out only when the renewal is refused or the retry is still a 401 (a network blip during the
 * renewal leaves the error to the page). A gate 403 `{ error }` signs out with that reason.
 */
export async function authFetch(
  request: Request,
  init: RequestInit | undefined,
  deps: { fetch?: typeof fetch; refresh?: () => Promise<RefreshResult>; signOut?: (reason: string) => Promise<void> } = {},
): Promise<Response> {
  const doFetch = deps.fetch ?? fetch;
  const refresh = deps.refresh ?? refreshAccessToken;
  const out = deps.signOut ?? signOut;
  const spare = request.clone();
  let res = await doFetch(request, init);
  if (res.status === 401) {
    const renewed = await refresh();
    if ('token' in renewed) {
      spare.headers.set('authorization', `Bearer ${renewed.token}`);
      res = await doFetch(spare, init);
    }
    const blip = 'error' in renewed && renewed.error === 'transient';
    if (res.status === 401 && !blip) void out('Your session ended. Sign in again.');
  } else if (res.status === 403) {
    // The gate answers `{ error }`; an oRPC procedure error answers `{ json: ... }` and is left to the caller.
    const body = (await res.clone().json().catch(() => null)) as { error?: unknown } | null;
    if (body && typeof body.error === 'string') void out(body.error);
  }
  return res;
}

export function describeRpcError(err: unknown): string {
  if (err instanceof ORPCError) {
    if (err.status === 401) return 'Your session ended. Sign in again.';
    if (err.status === 503) return 'The desk is not configured on the server.';
    return err.message || `Request failed (${err.code}).`;
  }
  if (err instanceof Error) return err.message;
  return 'Request failed.';
}
